import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
// iter264 — apply saved / OS theme BEFORE the first paint so users
// who prefer dark never see a light-mode flash on load. Safe to run
// at import time because `applyTheme` is a no-op in SSR.
import { applyTheme, resolveInitialTheme } from "@/lib/theme";
applyTheme(resolveInitialTheme());
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import SalesDashboard from "@/pages/SalesDashboard";
import Leads from "@/pages/Leads";
import Clients from "@/pages/Clients";
import Sites from "@/pages/Sites";
import Equipment from "@/pages/Equipment";
import Proposals from "@/pages/Proposals";
import InspectionConduct from "@/pages/InspectionConduct";
import COReview from "@/pages/COReview";
import ProposalReview from "@/pages/ProposalReview";
import WOReview from "@/pages/WOReview";
import SettingsPage from "@/pages/Settings";
import DispatchPage from "@/pages/Dispatch";
import MyWorkOrders from "@/pages/MyWorkOrders";
import MapView from "@/pages/MapView";
import WorkOrdersPage from "@/pages/WorkOrdersPage";
import WorkOrderDetailPage from "@/pages/WorkOrderDetailPage";
import TimesheetsPage from "@/pages/Timesheets";
import POReview from "@/pages/POReview";
import Purchasing from "@/pages/Purchasing";
import Invoicing from "@/pages/Invoicing";
import Profile from "@/pages/Profile";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import { initPushNotifications } from "@/lib/pushNotifications";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { I18nProvider } from "@/i18n";
import ProtectedRoute from "@/auth/ProtectedRoute";
import Login from "@/auth/Login";
import AuthCallback from "@/auth/AuthCallback";
import FieldLogin from "@/auth/FieldLogin";
import { useEffect } from "react";
import { toast } from "sonner";
import { applyServiceWorkerUpdate } from "@/lib/serviceWorker";
import { resolveLandingPath } from "@/lib/landingPages";
import { initInspectionSync } from "@/lib/inspectionSync";

// Page access is decided ONLY by the access matrix (Settings → Users →
// Permissions) via each route's `surface`.
const SETTINGS_SURFACES = ["settings.general","settings.users","settings.dispatch","settings.equipment","settings.vendors","settings.inspections","settings.geocoding","settings.wiki"];

/** Default landing — respects each user's chosen landing page (Settings → Users). */
function RoleHome() {
  const { user, can } = useAuth();
  const target = resolveLandingPath(user, can);
  if (target !== "/" && target !== "/ops") return <Navigate to={target} replace />;
  if (can("ops.dashboard", "read")) return <Navigate to="/ops" replace />;
  return <Navigate to="/field" replace />;
}

// Customer / vendor-facing review pages are outside the UI sweep: while one is mounted, <html> carries
// `abs-public` so they (and anything they portal) keep the previous type. Presentation only.
function PublicPage({ children }) {
  useEffect(() => {
    document.documentElement.classList.add("abs-public");
    return () => document.documentElement.classList.remove("abs-public");
  }, []);
  return children;
}

function App() {
  // Boot the MIRA console-capture ring buffer once at app boot so the
  // feedback widget can ship the last ~50 console lines as telemetry
  // when a user files a ticket.
  useEffect(() => {
    import("@/components/feedback/consoleCapture").then((m) => m.startConsoleCapture());
    // Offline inspection sync: boots app-wide so a queued inspection
    // uploads the moment signal returns, from whatever screen the tech
    // happens to be on (including a cold start after a phone reboot).
    initInspectionSync();
    // Native shell push (Capacitor) + browser/PWA web push foreground
    // toasts. Both are no-ops until the Firebase keys are in place.
    initPushNotifications({
      getUserId: () => {
        try { return JSON.parse(localStorage.getItem("user") || "{}")?.id || null; }
        catch { return null; }
      },
    });
    let unsubWebPush;
    import("@/lib/webPush").then(async (m) => {
      unsubWebPush = await m.listenForegroundWebPush();
    });
    return () => { if (typeof unsubWebPush === "function") unsubWebPush(); };
  }, []);

  // Service-worker "new version waiting" prompt. Surfaces as a sonner
  // toast with a "Refresh" action — tapping it activates the waiting
  // SW and reloads the page so techs always run the latest shell.
  //
  // iter186: handle the race where `sw:update-available` fires from
  // /lib/serviceWorker.js BEFORE React mounts (which is the common
  // case on a hard reload). Inside the useEffect we also poll the
  // registration once — if a waiting worker already exists, fire the
  // toast manually so the user still gets the prompt.
  useEffect(() => {
    const onUpdate = () => {
      toast("New version available", {
        description: "Refresh to get the latest fixes and features.",
        action: { label: "Refresh", onClick: () => applyServiceWorkerUpdate() },
        duration: Infinity,
        id: "sw-update",
      });
    };
    window.addEventListener("sw:update-available", onUpdate);
    // Catch-up check for the missed-event race.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg && reg.waiting && navigator.serviceWorker.controller) {
          onUpdate();
        }
      }).catch(() => {});
    }
    return () => window.removeEventListener("sw:update-available", onUpdate);
  }, []);

  return (
    <div className="app-layout">
      <BrowserRouter>
        <AuthProvider>
          <I18nProvider>
          <Routes>
            {/* Public — no auth required */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/field-login" element={<FieldLogin />} />
            <Route path="co-review/:token/:doc?" element={<PublicPage><COReview /></PublicPage>} />
            <Route path="proposal-review/:token/:doc?" element={<PublicPage><ProposalReview /></PublicPage>} />
            <Route path="wo-review/:token/:doc?" element={<PublicPage><WOReview /></PublicPage>} />
            {/* #65 — Vendor-facing PO review page (no auth; short_code is bearer) */}
            <Route path="po-review/:code" element={<PublicPage><POReview /></PublicPage>} />

            {/* Protected — everything else */}
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<RoleHome />} />
              <Route path="ops" element={<ProtectedRoute surface="ops.dashboard"><Dashboard /></ProtectedRoute>} />
              <Route path="sales" element={<ProtectedRoute surface="sales.dashboard"><SalesDashboard /></ProtectedRoute>} />
              {/* #pre-existing-bookmark-trap (iter180): direct
                  navigation to /dashboard used to land on a blank
                  page because Dashboard lives at the index '/'.
                  Redirect to root so old bookmarks keep working. */}
              <Route path="dashboard" element={<Navigate to="/ops" replace />} />
              <Route path="leads" element={<ProtectedRoute surface="sales.leads"><Leads /></ProtectedRoute>} />
              <Route path="clients" element={<ProtectedRoute surface="sales.customers"><Clients /></ProtectedRoute>} />
              <Route path="clients/:clientId/sites" element={<ProtectedRoute surface="sales.customers"><Sites /></ProtectedRoute>} />
              <Route path="clients/:clientId/equipment" element={<ProtectedRoute surface="sales.customers"><Equipment /></ProtectedRoute>} />
              <Route path="sites" element={<ProtectedRoute surface="sales.customers"><Sites /></ProtectedRoute>} />
              <Route path="equipment" element={<Navigate to="/settings" state={{ tab: "equipment" }} replace />} />
              <Route path="inspections" element={<Navigate to="/settings" state={{ tab: "inspections" }} replace />} />
              {/* #79 (M4): InspectionConduct lets the operator edit
                  inspection findings and roll them into proposals — admin
                  / PM / AM / SM / office only. Techs/viewers must not
                  reach this UI. */}
              <Route path="inspections/:id" element={<ProtectedRoute surface="ops.inspections"><InspectionConduct /></ProtectedRoute>} />
              <Route path="proposals" element={<ProtectedRoute surface="sales.proposals"><Proposals /></ProtectedRoute>} />
              <Route path="work-orders" element={<ProtectedRoute surface="ops.work_orders"><WorkOrdersPage /></ProtectedRoute>} />
              <Route path="work-orders/:id" element={<ProtectedRoute surface="ops.work_orders"><WorkOrderDetailPage /></ProtectedRoute>} />
              {/* iter268 — Settings is no longer admin-only: the access matrix
                  decides which sub-tabs each role sees (e.g. Service Manager
                  gets General / Equipment / Inspections / Dispatch). */}
              <Route path="settings" element={<ProtectedRoute surfaces={SETTINGS_SURFACES}><SettingsPage /></ProtectedRoute>} />
              <Route path="dispatch" element={<ProtectedRoute surface="ops.dispatch"><DispatchPage /></ProtectedRoute>} />
              <Route path="field" element={<ProtectedRoute surface="ops.tech"><MyWorkOrders /></ProtectedRoute>} />
              <Route path="timesheets" element={<ProtectedRoute surface="ops.timesheets"><TimesheetsPage /></ProtectedRoute>} />
              <Route path="purchasing" element={<ProtectedRoute surfaces={["ops.purchasing", "ops.warehouse"]}><Purchasing /></ProtectedRoute>} />
              <Route path="warehouse" element={<Navigate to="/purchasing?tab=receiving" replace />} />
              <Route path="invoicing" element={<ProtectedRoute surface="ops.invoices"><Invoicing /></ProtectedRoute>} />
              <Route path="map" element={<ProtectedRoute surface="ops.map"><MapView /></ProtectedRoute>} />
              {/* MIRA — every signed-in user can see their own ticket history */}
              <Route path="profile" element={<Profile />} />
              {/* #pre-existing-bookmark-trap-2 (iter181): catch-all
                  fallback for unknown paths inside the authenticated
                  Layout. Previously these silently rendered blank
                  (no console output, no NotFound). Redirect to the
                  index so /foo, /clients/typo, etc. land on the
                  dashboard instead of a dead page. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </I18nProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
      {/* #55 — PWA install banner (mobile only; iOS Safari shows manual hint) */}
      <PWAInstallBanner />
    </div>
  );
}

export default App;
